import { Link } from "react-router-dom";
import {
  Shield, Car, Video, DoorOpen, CalendarCheck, ShoppingBag, HardHat,
  KeyRound, UserCheck, Building2, ChevronRight, Dog, Warehouse, Hotel, Building, School, HeartPulse,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, CtaBand, SectionHead } from "@/components/Blocks";
import { SERVICES, IMAGES } from "@/data/contentEngine";

const SERVICE_ICONS = {
  "manned-guarding": Shield,
  "mobile-patrols": Car,
  "cctv-monitoring": Video,
  "door-supervision": DoorOpen,
  "event-security": CalendarCheck,
  "retail-security": ShoppingBag,
  "construction-site-security": HardHat,
  "key-holding-alarm-response": KeyRound,
  "close-protection": UserCheck,
  "corporate-security": Building2,
  "k9-security-patrols": Dog,
  "warehouse-industrial-security": Warehouse,
  "hotel-hospitality-security": Hotel,
  "vacant-property-security": Building,
  "school-campus-security": School,
  "hospital-healthcare-security": HeartPulse,
};

const ServicesIndex = () => (
  <>
    <Seo
      title="Security Services Northern Ireland | SIA Guards, Patrols & CCTV | Xtreme Protection"
      description="Explore all 16 specialist security services across Northern Ireland: manned guarding, mobile patrols, CCTV monitoring, door supervision, event, retail, construction, corporate, K9, warehouse, hotel, vacant property, school & healthcare security."
      path="/services"
    />
    <PageHero
      eyebrow="Security Services Northern Ireland"
      title="Security services for every risk"
      sub="Sixteen specialist security disciplines, delivered by SIA-licensed, BS7858-vetted officers in every city and town across Northern Ireland."
      image={IMAGES.patrol}
      testId="services-index-hero"
    />
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-12">
      <SectionHead
        eyebrow="All Services"
        title="Choose your protection"
        testId="services-index-heading"
      />
      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2">
        {SERVICES.map((s, i) => {
          const Icon = SERVICE_ICONS[s.slug];
          return (
            <Reveal key={s.slug} delay={(i % 2) * 0.08}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`services-index-card-${s.slug}`}
                className="tactical-card group flex h-full gap-5 rounded-md p-6"
              >
                <div className="relative hidden h-24 w-24 shrink-0 overflow-hidden rounded-sm sm:block">
                  <img src={s.image} alt={`${s.title} in Northern Ireland`} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <Icon className="h-6 w-6 text-amber-500" />
                    <ChevronRight className="h-4 w-4 text-slate-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-amber-500" />
                  </div>
                  <h2 className="mt-3 font-display text-xl font-bold uppercase tracking-wide text-slate-100">
                    {s.title}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.short}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {s.keywords.slice(0, 3).map((k) => (
                      <span key={k} className="rounded-sm border border-slate-700 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                        {k}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
    <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-12">
      <CtaBand
        title="Not sure which service fits your risk?"
        sub="Call us — a free risk assessment will identify exactly what your premises need, with a transparent quotation and no obligation."
      />
    </section>
  </>
);

export default ServicesIndex;
